import * as React from 'react';

import PlaidConnectionModal from '../../components/plaid/PlaidConnectionModal';
import { useAccountLink } from '../../components/plaid/useAccountLink';

/**
 * Outcome the browser reports back for an `financial_account_connect` invocation —
 * sent verbatim as the `client_tool_result` payload and read by the model as
 * the tool result. `linked` carries how many accounts the user connected;
 * `cancelled` means they closed the flow without linking.
 */
export interface IAddFinancialAccountResult {
  status: 'linked' | 'cancelled';
  accountsAdded?: number;
}

interface IAssistantAddAccountFlowProps {
  toolId: string;
  /**
   * Report the outcome to the suspended agent turn. `result` is the structured
   * payload the model reads — here an {@link IAddFinancialAccountResult}; `error`
   * is set instead when the flow couldn't run at all.
   */
  onResult: (toolId: string, result?: unknown, error?: string) => void;
}

/**
 * The assistant's `financial_account_connect` browser flow. On mount it opens the
 * same "Add account" connection-selection modal the accounts page uses — pick
 * an already-connected institution (reconnection) or connect a new one — then
 * runs Plaid via the shared {@link useAccountLink} hook, so duplicate handling
 * and reconnection behave identically to the onboarding button. Reports
 * `linked` / `cancelled` exactly once back to the suspended agent turn; link
 * errors (e.g. a duplicate institution) re-surface the modal rather than ending
 * the flow, so they're never reported as a failure.
 */
const AssistantAddAccountFlow = ({ toolId, onResult }: IAssistantAddAccountFlowProps) => {
  // Report at most once — onLinked, onCancelled, and an unmount can race, but
  // the agent turn resumes on the first result and ignores the rest.
  const reportedRef = React.useRef(false);
  const report = React.useCallback(
    (result: IAddFinancialAccountResult) => {
      if (reportedRef.current) {
        return;
      }
      reportedRef.current = true;
      onResult(toolId, result);
    },
    [toolId, onResult]
  );

  const { trigger, isModalOpen, onModalOpenChange, onModalSelect, errorCode } = useAccountLink({
    onLinked: (accounts) => report({ status: 'linked', accountsAdded: accounts.length }),
    onCancelled: () => report({ status: 'cancelled' }),
  });

  // Open the connection-selection modal as soon as the flow mounts.
  React.useEffect(() => {
    trigger();
  }, [trigger]);

  return (
    <PlaidConnectionModal
      isOpen={isModalOpen}
      onOpenChange={onModalOpenChange}
      onSelect={onModalSelect}
      errorCode={errorCode}
    />
  );
};

export default AssistantAddAccountFlow;
